import React, { useState, useRef, useEffect } from 'react';
import { Lock, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { mobilePageBackground } from '../../../services/mobileStyleService';
import { getBaseballBackground } from './baseballAssets';

const MobileResetPasswordStep2 = ({ onResetPassword, onBack, email, loading, error }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [passwords, setPasswords] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const inputRefs = useRef([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const digits = pastedData.match(/\d/g) || [];
    
    const newCode = [...code];
    digits.forEach((digit, i) => {
      if (i < 6) {
        newCode[i] = digit;
      }
    });
    setCode(newCode);

    const nextEmptyIndex = newCode.findIndex(c => c === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handlePasswordChange = (e) => {
    setPasswords({
      ...passwords,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const resetCode = code.join('');
    if (resetCode.length !== 6) return;
    if (passwords.newPassword !== passwords.confirmPassword) return;
    if (passwords.newPassword.length < 8) return;

    onResetPassword(email, resetCode, passwords.newPassword);
  };

  const isCodeComplete = code.every(digit => digit !== '');
  const passwordsMatch = passwords.confirmPassword === '' || passwords.newPassword === passwords.confirmPassword;
  const canSubmit = isCodeComplete && passwordsMatch && passwords.newPassword.length >= 8 && passwords.confirmPassword !== '';

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ background: mobilePageBackground, ...getBaseballBackground() }}
    >
      {/* Header */}
      <div className={`flex items-center justify-between px-6 py-4 border-b ${dynastyTheme.classes.border.neutral}`}>
        <button
          onClick={onBack}
          className={`flex items-center gap-2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        <h2 className={`text-lg font-semibold ${dynastyTheme.classes.text.white}`}>
          Reset Password
        </h2>
        <div className="w-16"></div>
      </div>

      {/* Form */}
      <div className="flex-1 px-6 py-8 overflow-y-auto">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto">
          {/* Icon */}
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🔐</div>
            <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-3`}>
              Enter Reset Code
            </h3>
            <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter}`}>
              Check your email for the 6-digit code
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className={`${dynastyTheme.components.badge.error} p-4 rounded-lg text-center`}>
              {error}
            </div>
          )}

          {/* Reset Code */}
          <div>
            <label className={`${dynastyTheme.components.label} text-center block`}>
              Reset Code
            </label>
            <div className="flex gap-2 justify-center" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`w-12 h-14 text-center text-2xl font-bold rounded-md border-2 ${dynastyTheme.classes.bg.neutral} ${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}
                    ${digit ? `${dynastyTheme.classes.border.primary}` : `${dynastyTheme.classes.border.neutralLight}`}
                    focus:${dynastyTheme.classes.border.primary} focus:ring-2 focus:ring-yellow-400/20
                  `}
                />
              ))}
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className={dynastyTheme.components.label}>
              New Password
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type={showPassword ? 'text' : 'password'}
                name="newPassword"
                value={passwords.newPassword}
                onChange={handlePasswordChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-12 py-3 text-base`}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className={`text-xs ${dynastyTheme.classes.text.neutralLighter} mt-1`}>
              Must be at least 8 characters
            </p>
          </div>

          {/* Confirm New Password */}
          <div>
            <label className={dynastyTheme.components.label}>
              Confirm New Password
            </label>
            <div className="relative">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${dynastyTheme.classes.text.neutralLighter}`} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={passwords.confirmPassword}
                onChange={handlePasswordChange}
                className={`${dynastyTheme.components.input} w-full pl-12 pr-12 py-3 text-base ${
                  !passwordsMatch ? 'border-red-500' : ''
                }`}
                placeholder="••••••••"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={`absolute right-3 top-1/2 -translate-y-1/2 ${dynastyTheme.classes.text.neutralLighter} hover:${dynastyTheme.classes.text.white} ${dynastyTheme.classes.transition}`}
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {!passwordsMatch && passwords.confirmPassword && (
              <p className={`text-sm ${dynastyTheme.classes.text.error} mt-2`}>
                Passwords do not match
              </p>
            )}
          </div>

          {/* Reset Password Button */}
          <button
            type="submit"
            disabled={loading || !canSubmit}
            className={`${dynastyTheme.utils.getComponent('button', 'primary', 'lg')} w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <>Resetting...</>
            ) : (
              <>
                <span>🔐</span>
                Reset Password
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default MobileResetPasswordStep2;
